import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import ReCAPTCHA from 'react-google-recaptcha';
import { getRecaptchaSiteKey } from '../../../Constants/RecaptchaConfig';
import styles from './AuthModal.module.scss';
import GoogleLogo from '../../../Assets/LoginScreen/googleLogo.svg';
import useAuthModalLogic from './useAuthModalLogic';
import { MERCHANT_REDIRECTION_LINK } from '../../../Constants/Config';
import eventEmitter from '../../Home/eventEmitter';

const AuthModal = ({
  isOpen,
  onClose,
  initialTab = 'signin',
  postLoginStoreType = '',
  postLoginCategoryName = '',
  onPostLoginCategoryNavigate,
}) => {
  const { isAuthenticated } = useSelector((state) => state.authUser);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [cityStateZip, setCityStateZip] = useState('');
  const [showAddressDropdown, setShowAddressDropdown] = useState(false);
  const addressInputRef = useRef(null);
  const modalRef = useRef(null);
  const contentRef = useRef(null);
  const closeButtonRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const resetFormsRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const {
    isLoading,
    errorMessage,
    setErrorMessage,
    loginValues,
    registerValues,
    handleLoginInputChange,
    handleRegisterInputChange,
    handleUserLogin,
    handleUserRegister,
    loginWithGoogle,
    resetForms,
    // Address autocomplete
    addressSearchInput,
    addressPlaces,
    addressLoading,
    selectedAddress,
    addressError,
    setAddressError,
    handleAddressInputChange,
    handleAddressPlaceSelect,
    // Google login with address
    completeGoogleLoginWithAddress,
    // reCAPTCHA
    recaptchaError,
    handleRecaptchaChange,
  } = useAuthModalLogic(
    onClose,
    setActiveTab,
    postLoginStoreType,
    postLoginCategoryName,
    onPostLoginCategoryNavigate,
  );
  resetFormsRef.current = resetForms;

  useEffect(() => {
    if (!isOpen) {
      resetFormsRef.current?.();
      setShowPassword(false);
      setShowConfirmPassword(false);
      setShowAddressDropdown(false);
      setCityStateZip('');
    } else {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocusedElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousBodyOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';
    const focusFrame = window.requestAnimationFrame(() => closeButtonRef.current?.focus());

    const handleDialogKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab' || !modalRef.current) return;

      const focusableElements = Array.from(
        modalRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.offsetParent !== null);

      if (focusableElements.length === 0) {
        event.preventDefault();
        modalRef.current.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleDialogKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleDialogKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      previouslyFocusedElement?.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    contentRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    closeButtonRef.current?.focus();
  }, [activeTab, isOpen]);

  // Show dropdown when there are suggestions
  useEffect(() => {
    if (addressPlaces.length > 0) {
      setShowAddressDropdown(true);
    } else {
      setShowAddressDropdown(false);
    }
  }, [addressPlaces]);

  const handleAddressSelect = (place) => {
    handleAddressPlaceSelect(place);
    setShowAddressDropdown(false);
  };

  // When a place is selected and details are fetched, populate the City, State, ZIP field
  useEffect(() => {
    if (selectedAddress) {
      const { city, state, zipCode } = selectedAddress;
      const parts = [];
      if (city) parts.push(city);
      if (state) parts.push(state);
      if (zipCode) parts.push(zipCode);
      setCityStateZip(parts.join(', '));
    }
  }, [selectedAddress]);

  if (!isOpen) return null;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setErrorMessage('');
  };

  const handleFindStores = (e) => {
    e.preventDefault();

    // Require a selected address from suggestions before proceeding
    if (!selectedAddress) {
      setAddressError('Please select an address from the suggestions.');
      return;
    }

    // Save location to cookies (same pattern as LandingLocationModal)
    const coords = {
      lat: selectedAddress.latitude,
      lng: selectedAddress.longitude,
    };

    Cookies.set('userLocation', JSON.stringify(coords), { expires: 7 });
    Cookies.set('zipCode', JSON.stringify(selectedAddress.zipCode), {
      expires: 7,
    });

    const newAddress = {
      unique_id: Date.now(),
      a_address_line_1: selectedAddress.streetAddress,
      a_zip: selectedAddress.zipCode,
      a_city: selectedAddress.city,
      a_state: selectedAddress.state,
      latitude: selectedAddress.latitude,
      longitude: selectedAddress.longitude,
      placeID: selectedAddress.placeId,
      is_default: '1',
    };

    const existing = Cookies.get('customerAddresses');
    let addressArray = [newAddress]; // Put new address first
    if (existing) {
      try {
        const parsed = JSON.parse(existing);
        if (Array.isArray(parsed)) {
          // Filter out duplicates by zipCode and mark as not default
          const others = parsed
            .filter((addr) => addr.a_zip !== selectedAddress.zipCode)
            .map((addr) => ({ ...addr, is_default: '0' }));
          addressArray = [newAddress, ...others];
        }
      } catch (err) {
        // Ignore parse errors and start fresh
      }
    }
    Cookies.set('customerAddresses', JSON.stringify(addressArray), {
      expires: 7,
    });

    eventEmitter.emit('locationUpdated');

    onClose();
    navigate('/home', { replace: false });
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.backdrop} onClick={onClose} />

      <div
        ref={modalRef}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
        tabIndex={-1}
      >
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          className={styles.closeBtn}
          aria-label="Close sign-in dialog"
        >
          <CloseIcon />
        </button>

        <div ref={contentRef} className={styles.content}>
          <h2 id="auth-modal-title" className={styles.title}>
            {activeTab === 'address'
              ? 'Enter your address'
              : activeTab === 'google-address'
              ? 'Set your delivery Address'
              : 'Sign in or Sign up'}
          </h2>

          {activeTab !== 'google-address' && (
            <div className={styles.infoBanner}>
              <AutoAwesomeIcon className={styles.infoIcon} />
              <p>
                {activeTab === 'address'
                  ? 'Enter your address to find stores near you'
                  : 'Sign in to save your favorite stores and track orders'}
              </p>
            </div>
          )}

          {activeTab === 'address' ? (
            <form onSubmit={handleFindStores}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Delivery Address</label>
                <div className={styles.addressInputWrapper}>
                  <LocationOnOutlinedIcon className={styles.addressIcon} />
                  <input
                    ref={addressInputRef}
                    type="text"
                    placeholder="Enter your address"
                    value={addressSearchInput}
                    onChange={handleAddressInputChange}
                    onFocus={() => {
                      if (addressPlaces.length > 0) setShowAddressDropdown(true);
                    }}
                    onBlur={() => setTimeout(() => setShowAddressDropdown(false), 150)}
                    className={`${styles.input} ${styles.addressInput} ${addressError ? styles.inputError : ''}`}
                    autoComplete="off"
                  />
                  {addressLoading && (
                    <div className={styles.addressLoader}>
                      <CircularProgress size={16} />
                    </div>
                  )}
                  {showAddressDropdown && addressPlaces.length > 0 && (
                    <ul className={styles.addressSuggestions}>
                      {addressPlaces.map((place, index) => {
                        const structured = place?.placePrediction?.structuredFormat;
                        const mainText = structured?.mainText?.text || '';
                        const secondaryText = structured?.secondaryText?.text || '';

                        return (
                          <li
                            key={index}
                            className={styles.addressSuggestionItem}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleAddressSelect(place);
                            }}
                          >
                            <LocationOnOutlinedIcon style={{ fontSize: 16, color: '#9ca3af', marginRight: 8, flexShrink: 0 }} />
                            <span className={styles.suggestionContent}>
                              <span className={styles.suggestionPrimary}>{mainText}</span>
                              {secondaryText && (
                                <span className={styles.suggestionSecondary}>{secondaryText}</span>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="City, State, ZIP"
                  value={cityStateZip}
                  onChange={(e) => setCityStateZip(e.target.value)}
                  className={`${styles.input} ${styles.addressSecondary}`}
                />
                {addressError && (
                  <span className={styles.errorText}>{addressError}</span>
                )}
                {selectedAddress && (
                  <span className={styles.successText}>✓ Address selected</span>
                )}
              </div>

              <button type="submit" className={styles.primaryBtn}>
                Find Stores Near Me
              </button>

              {!isAuthenticated && (
                <>
                  <div className={styles.divider}>
                    <span>or</span>
                  </div>

                  <div className={styles.buttonGroup}>
                    <button
                      type="button"
                      onClick={() => setActiveTab('signin')}
                      className={styles.outlineBtn}
                    >
                      Sign In
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('signup')}
                      className={styles.outlineBtn}
                    >
                      Sign Up
                    </button>
                  </div>
                </>
              )}
            </form>
          ) : activeTab === 'signin' ? (
            <>
              <div className={styles.tabToggle}>
                <button
                  type="button"
                  onClick={() => handleTabChange('signin')}
                  className={`${styles.tabBtn} ${styles.active}`}
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => handleTabChange('signup')}
                  className={styles.tabBtn}
                >
                  Sign Up
                </button>
              </div>

              <form onSubmit={handleUserLogin}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Email</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    autoComplete="email"
                    value={loginValues.email}
                    onChange={handleLoginInputChange}
                    className={`${styles.input} ${loginValues.errors.email ? styles.inputError : ''}`}
                    disabled={isLoading}
                  />
                  {loginValues.errors.email && (
                    <span className={styles.errorText}>{loginValues.errors.email}</span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Password</label>
                  <div className={styles.passwordWrapper}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      value={loginValues.password}
                      onChange={handleLoginInputChange}
                      className={`${styles.input} ${loginValues.errors.password ? styles.inputError : ''}`}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </button>
                  </div>
                  {loginValues.errors.password && (
                    <span className={styles.errorText}>{loginValues.errors.password}</span>
                  )}
                </div>

                {errorMessage && (
                  <div className={styles.errorBanner}>{errorMessage}</div>
                )}

                <Link to="/forgot-password" className={styles.forgotPassword} onClick={onClose}>
                  Forgot Password?
                </Link>

                <button type="submit" className={styles.primaryBtn} disabled={isLoading}>
                  {isLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Sign In'}
                </button>
              </form>

              <div className={styles.divider}>
                <span>Or Login with</span>
              </div>

              <div className={styles.socialButtons}>
                <button
                  type="button"
                  onClick={() => loginWithGoogle()}
                  className={styles.googleBtn}
                  disabled={isLoading}
                >
                  <img src={GoogleLogo} alt="Google" className={styles.socialIcon} />
                  Continue with Google
                </button>
              </div>

              <p className={styles.merchantLink}>
                Are you a Merchant?{' '}
                <Link
                  className="login-customer-reduirection-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  to={MERCHANT_REDIRECTION_LINK}
                >
                  Login
                </Link>
              </p>

              <div className={styles.divider}>
                <span>or</span>
              </div>

              <button
                type="button"
                onClick={() => handleTabChange('address')}
                className={styles.outlineBtnFull}
              >
                Enter Address Instead
              </button>
            </>
          ) : activeTab === 'signup' ? (
            <>
              <div className={styles.tabToggle}>
                <button
                  type="button"
                  onClick={() => handleTabChange('signin')}
                  className={styles.tabBtn}
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => handleTabChange('signup')}
                  className={`${styles.tabBtn} ${styles.active}`}
                >
                  Sign Up
                </button>
              </div>

              <form onSubmit={handleUserRegister}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>First Name *</label>
                    <input
                      type="text"
                      name="firstname"
                      placeholder="First name"
                      autoComplete="given-name"
                      value={registerValues.firstname}
                      onChange={handleRegisterInputChange}
                      className={`${styles.input} ${registerValues.errors.firstname ? styles.inputError : ''}`}
                      disabled={isLoading}
                    />
                    {registerValues.errors.firstname && (
                      <span className={styles.errorText}>{registerValues.errors.firstname}</span>
                    )}
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Last Name</label>
                    <input
                      type="text"
                      name="lastname"
                      placeholder="Last name"
                      autoComplete="family-name"
                      value={registerValues.lastname}
                      onChange={handleRegisterInputChange}
                      className={`${styles.input} ${registerValues.errors.lastname ? styles.inputError : ''}`}
                      disabled={isLoading}
                    />
                    {registerValues.errors.lastname && (
                      <span className={styles.errorText}>{registerValues.errors.lastname}</span>
                    )}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Email *</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    autoComplete="email"
                    value={registerValues.email}
                    onChange={handleRegisterInputChange}
                    className={`${styles.input} ${registerValues.errors.email ? styles.inputError : ''}`}
                    disabled={isLoading}
                  />
                  {registerValues.errors.email && (
                    <span className={styles.errorText}>{registerValues.errors.email}</span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="10-digit phone number"
                    autoComplete="tel"
                    value={registerValues.phone}
                    onChange={handleRegisterInputChange}
                    maxLength={10}
                    className={`${styles.input} ${registerValues.errors.phone ? styles.inputError : ''}`}
                    disabled={isLoading}
                  />
                  {registerValues.errors.phone && (
                    <span className={styles.errorText}>{registerValues.errors.phone}</span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Password *</label>
                  <div className={styles.passwordWrapper}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="Create a password"
                      autoComplete="new-password"
                      value={registerValues.password}
                      onChange={handleRegisterInputChange}
                      className={`${styles.input} ${registerValues.errors.password ? styles.inputError : ''}`}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </button>
                  </div>
                  {registerValues.errors.password && (
                    <span className={styles.errorText}>{registerValues.errors.password}</span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Confirm Password *</label>
                  <div className={styles.passwordWrapper}>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmpassword"
                      placeholder="Re-enter password"
                      autoComplete="new-password"
                      value={registerValues.confirmpassword}
                      onChange={handleRegisterInputChange}
                      className={`${styles.input} ${registerValues.errors.confirmpassword ? styles.inputError : ''}`}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                    >
                      {showConfirmPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </button>
                  </div>
                  {registerValues.errors.confirmpassword && (
                    <span className={styles.errorText}>{registerValues.errors.confirmpassword}</span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Address *</label>
                  <div className={styles.addressInputWrapper}>
                    <LocationOnOutlinedIcon className={styles.addressIcon} />
                    <input
                      ref={addressInputRef}
                      type="text"
                      placeholder="Enter your address"
                      value={addressSearchInput}
                      onChange={handleAddressInputChange}
                      onFocus={() => { if (addressPlaces.length > 0) setShowAddressDropdown(true); }}
                      onBlur={() => setTimeout(() => setShowAddressDropdown(false), 150)}
                      className={`${styles.input} ${styles.addressInput} ${addressError ? styles.inputError : ''}`}
                      disabled={isLoading}
                      autoComplete="street-address"
                    />
                    {addressLoading && (
                      <div className={styles.addressLoader}>
                        <CircularProgress size={16} />
                      </div>
                    )}
                    {showAddressDropdown && addressPlaces.length > 0 && (
                      <ul className={styles.addressSuggestions}>
                        {addressPlaces.map((place, index) => {
                          const structured = place?.placePrediction?.structuredFormat;
                          const mainText = structured?.mainText?.text || '';
                          const secondaryText = structured?.secondaryText?.text || '';
                          return (
                            <li
                              key={index}
                              className={styles.addressSuggestionItem}
                              onMouseDown={(e) => { e.preventDefault(); handleAddressSelect(place); }}
                            >
                              <LocationOnOutlinedIcon style={{ fontSize: 16, color: '#9ca3af', marginRight: 8, flexShrink: 0 }} />
                              <span className={styles.suggestionContent}>
                                <span className={styles.suggestionPrimary}>{mainText}</span>
                                {secondaryText && (
                                  <span className={styles.suggestionSecondary}>{secondaryText}</span>
                                )}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                  {addressError && (
                    <span className={styles.errorText}>{addressError}</span>
                  )}
                  {selectedAddress && (
                    <span className={styles.successText}>✓ Address selected</span>
                  )}
                </div>

                <div className={`${styles.formGroup} ${styles.recaptchaWrap}`}>
                  <ReCAPTCHA
                    sitekey={getRecaptchaSiteKey()}
                    onChange={handleRecaptchaChange}
                  />
                  {recaptchaError && (
                    <span className={styles.errorText}>{recaptchaError}</span>
                  )}
                </div>

                {errorMessage && (
                  <div className={styles.errorBanner}>{errorMessage}</div>
                )}

                <button type="submit" className={styles.primaryBtn} disabled={isLoading}>
                  {isLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Sign Up'}
                </button>
              </form>

              <p className={styles.terms}>
                By signing up, you agree to Quickvee's{' '}
                <Link to="/terms-condition" onClick={onClose}>Terms</Link> and{' '}
                <Link to="/privacy-policy" onClick={onClose}>Privacy Policy</Link>.
              </p>

              <div className={styles.divider}>
                <span>or</span>
              </div>

              <button
                type="button"
                onClick={() => handleTabChange('address')}
                className={styles.outlineBtnFull}
              >
                Enter Address Instead
              </button>
            </>
          ) : activeTab === 'google-address' ? (
            <>
              <div className={styles.infoBanner} style={{ marginBottom: '1rem' }}>
                <AutoAwesomeIcon className={styles.infoIcon} />
                <p>Welcome! Please set your delivery address to continue.</p>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Your Address *</label>
                <div className={styles.addressInputWrapper}>
                  <LocationOnOutlinedIcon className={styles.addressIcon} />
                  <input
                    ref={addressInputRef}
                    type="text"
                    placeholder="Enter your address"
                    value={addressSearchInput}
                    onChange={handleAddressInputChange}
                    onFocus={() => { if (addressPlaces.length > 0) setShowAddressDropdown(true); }}
                    onBlur={() => setTimeout(() => setShowAddressDropdown(false), 150)}
                    className={`${styles.input} ${styles.addressInput} ${addressError ? styles.inputError : ''}`}
                    disabled={isLoading}
                    autoComplete="off"
                  />
                  {addressLoading && (
                    <div className={styles.addressLoader}>
                      <CircularProgress size={16} />
                    </div>
                  )}
                  {showAddressDropdown && addressPlaces.length > 0 && (
                    <ul className={styles.addressSuggestions}>
                      {addressPlaces.map((place, index) => {
                        const structured = place?.placePrediction?.structuredFormat;
                        const mainText = structured?.mainText?.text || '';
                        const secondaryText = structured?.secondaryText?.text || '';

                        return (
                          <li
                            key={index}
                            className={styles.addressSuggestionItem}
                            onMouseDown={(e) => { e.preventDefault(); handleAddressSelect(place); }}
                          >
                            <LocationOnOutlinedIcon style={{ fontSize: 16, color: '#9ca3af', marginRight: 8, flexShrink: 0 }} />
                            <span className={styles.suggestionContent}>
                              <span className={styles.suggestionPrimary}>{mainText}</span>
                              {secondaryText && (
                                <span className={styles.suggestionSecondary}>{secondaryText}</span>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                {addressError && (
                  <span className={styles.errorText}>{addressError}</span>
                )}
                {selectedAddress && (
                  <span className={styles.successText}>✓ Address selected</span>
                )}
              </div>

              {errorMessage && (
                <div className={styles.errorBanner}>{errorMessage}</div>
              )}

              <button
                type="button"
                className={styles.primaryBtn}
                disabled={isLoading || !selectedAddress}
                onClick={completeGoogleLoginWithAddress}
              >
                {isLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Continue to Quickvee'}
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
